import { useEffect, useRef } from 'react';
import { Box, useMediaQuery, useTheme } from '@mui/material';

import { createJotEngine, JOT_VIEWBOX, type JotEngine } from '../jotEngine';

export interface JotCharacterProps {
  mood?: 'idle' | 'sending' | 'sent';
  reaction?: number;
  reducedMotion?: boolean;
  size?: number | string;
  height?: number | string;
}

/** Recovered from codex/jot-little-guy-frontend at 41e4b5c8ef. */
export function JotCharacter({
  mood = 'idle',
  reaction = 0,
  reducedMotion = false,
  size = 88,
  height = size,
}: JotCharacterProps) {
  const svg = useRef<SVGSVGElement>(null);
  const engine = useRef<JotEngine | null>(null);
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const isStatic = reducedMotion || prefersReducedMotion;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTouchPointer = useMediaQuery('(pointer: coarse)');
  const trackPointer = !isMobile && !isTouchPointer;

  useEffect(() => {
    if (!svg.current || isStatic) return;
    const character = createJotEngine(svg.current);
    engine.current = character;
    const pointer = (event: PointerEvent) => {
      if (event.pointerType !== 'touch') character.setPointer(event.clientX, event.clientY);
    };
    if (trackPointer) window.addEventListener('pointermove', pointer, { passive: true });
    return () => {
      window.removeEventListener('pointermove', pointer);
      character.destroy();
      engine.current = null;
    };
  }, [isStatic, trackPointer]);

  useEffect(() => {
    engine.current?.setState(
      mood === 'sending' ? 'working' : mood === 'sent' ? 'approved' : 'idle',
    );
  }, [mood, isStatic, trackPointer]);
  useEffect(() => {
    if (reaction > 0) {
      engine.current?.hop();
      engine.current?.spin();
    }
  }, [reaction]);

  return (
    <Box
      component="svg"
      key={String(isStatic)}
      ref={svg}
      viewBox={JOT_VIEWBOX}
      data-jot-motion={isStatic ? 'static' : 'animated'}
      aria-hidden="true"
      focusable="false"
      sx={{ width: size, height, display: 'block', flexShrink: 0, overflow: 'visible' }}
    >
      {isStatic && (
        <g>
          <rect x="85" y="97" width="150" height="150" rx="30" fill="#1C1E85" />
          <path
            d={
              mood === 'sent'
                ? 'M120 157Q133 140 146 157M174 157Q187 140 200 157'
                : 'M133 140V170M187 140V170'
            }
            fill="none"
            stroke="white"
            strokeWidth="13"
            strokeLinecap="round"
          />
        </g>
      )}
    </Box>
  );
}
